// CPC — connect via RWAX Wallet extension (Wallet Standard + window.cpcWallet fallback).

import {
  useConnectWallet,
  useCurrentAccount,
  useCurrentWallet,
  useDisconnectWallet,
  useWallets,
} from "@mysten/dapp-kit";
import { Button, Text } from "@mysten/ui";
import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { WALLET_EXTENSION_URL } from "~/config/cpcWalletLinks";
import { useT } from "~/i18n/LocaleContext";

/** Canonical Wallet Standard name (must match wallet-extension injected.ts). */
export const CPC_WALLET_NAME = "RWAX Wallet";

/** Accept legacy builds that still register as "DGC Wallet" / "CPC Wallet". */
const CPC_WALLET_NAME_ALIASES = new Set([CPC_WALLET_NAME, "DGC Wallet", "CPC Wallet"]);

type DappKitWallet = ReturnType<typeof useWallets>[number];

type WindowWithCpc = Window & { cpcWallet?: DappKitWallet };

function isCpcWalletName(name: string | undefined | null): boolean {
  return !!name && CPC_WALLET_NAME_ALIASES.has(name);
}

function pickCpcWallet(listed: readonly DappKitWallet[]): DappKitWallet | null {
  const fromList = listed.find((w) => isCpcWalletName(w.name));
  if (fromList) return fromList;
  const injected = (window as WindowWithCpc).cpcWallet;
  if (isCpcWalletName(injected?.name)) return injected;
  return null;
}

export function CpcConnectButton({ variant = 'default' }: { variant?: 'default' | 'sidebar' }) {
  const t = useT();
  const listedWallets = useWallets();
  const account = useCurrentAccount();
  const { currentWallet } = useCurrentWallet();
  const { mutate: connect, isPending: connecting } = useConnectWallet();
  const { mutate: disconnect, isPending: disconnecting } =
    useDisconnectWallet();
  const [injectedReady, setInjectedReady] = useState(() =>
    isCpcWalletName((window as WindowWithCpc).cpcWallet?.name),
  );
  const [cpcWallet, setCpcWallet] = useState<DappKitWallet | null>(() =>
    pickCpcWallet(listedWallets),
  );

  useEffect(() => {
    const sync = () => {
      const inj = (window as WindowWithCpc).cpcWallet;
      setInjectedReady(isCpcWalletName(inj?.name));
      setCpcWallet(pickCpcWallet(listedWallets));
    };
    sync();
    const poll = window.setInterval(sync, 400);
    return () => window.clearInterval(poll);
  }, [listedWallets]);

  const hasCpc =
    !!cpcWallet ||
    injectedReady ||
    listedWallets.some((w) => isCpcWalletName(w.name));

  const onConnect = useCallback(() => {
    const w = pickCpcWallet(listedWallets) ?? pickCpcWallet([]);
    if (!w) {
      window.alert(t('wallet.notFoundAlert'));
      return;
    }
    connect({ wallet: w });
  }, [connect, listedWallets, t]);

  const sidebarBtnClass =
    'w-full min-h-[40px] rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200';

  const sidebarLinkBtnClass =
    'inline-flex w-full items-center justify-center rounded-lg border px-3 py-2 text-xs font-semibold no-underline transition-colors';

  const connectLabel = connecting ? t('common.connecting') : t('wallet.connect');
  const disconnectLabel = disconnecting ? t('wallet.disconnecting') : t('common.disconnect');

  if (!hasCpc) {
    const origin = window.location.origin;
    if (variant === 'sidebar') {
      return (
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5 space-y-2.5">
          <p className="text-xs font-medium text-[#e2e8f0]">{t('wallet.notDetected')}</p>
          <p className="text-[11px] leading-relaxed text-[#94a3b8]">
            {t('wallet.installHint')}
          </p>
          <p className="text-[11px] leading-relaxed text-[#94a3b8]">
            {t('wallet.siteEnabledHint').replace('{origin}', origin)}
          </p>
          <div className="flex flex-col gap-1.5">
            <a
              href={WALLET_EXTENSION_URL}
              download
              target="_blank"
              rel="noopener noreferrer"
              className={clsx(
                sidebarLinkBtnClass,
                'border-[#38BDF8]/40 bg-[#38bff81a] text-[#38BDF8] hover:bg-[#38BDF8] hover:text-white',
              )}
            >
              {t('wallet.downloadExt')}
            </a>
            <Link
              to="/wallet"
              className={clsx(
                sidebarLinkBtnClass,
                'border-white/10 text-[#94a3b8] hover:border-white/20 hover:text-[#e2e8f0]',
              )}
            >
              {t('wallet.appsCenter')} →
            </Link>
          </div>
        </div>
      );
    }
    return (
      <div className="flex max-w-sm flex-col items-end gap-1 text-right">
        <Text
          variant="caption/medium"
          color="issue-dark"
          title="chrome://extensions → 开发者模式 → 加载已解压 wallet-extension/dist → 刷新本页"
        >
          {t('wallet.notDetectedExt')}
        </Text>
        <Text variant="caption/medium" color="steel-dark">
          {t('wallet.siteEnabledHint').replace('{origin}', origin)}
        </Text>
        <Text variant="caption/medium" color="steel-dark">
          window.cpcWallet=
          {(window as WindowWithCpc).cpcWallet?.name ?? "no"} · dapp-kit=
          {listedWallets.map((w) => w.name).join(", ") || "(empty)"}
        </Text>
      </div>
    );
  }

  if (account && isCpcWalletName(currentWallet?.name)) {
    if (variant === 'sidebar') {
      return (
        <button
          type="button"
          onClick={() => disconnect()}
          disabled={disconnecting}
          className={clsx(
            sidebarBtnClass,
            'border border-white/15 bg-white/5 text-[#e2e8f0] hover:border-[#38BDF8]/40 hover:bg-[#38BDF8]/10 hover:text-[#38BDF8] disabled:opacity-50',
          )}
        >
          {disconnectLabel}
        </button>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={() => disconnect()}
          disabled={disconnecting}
          className="bg-[#2394c4] hover:bg-[#38BDF8] text-white rounded-lg px-2 py-2"
        >
          {disconnectLabel}
        </Button>
      </div>
    );
  }

  if (account && currentWallet && !isCpcWalletName(currentWallet.name)) {
    if (variant === 'sidebar') {
      return (
        <div className="space-y-2">
          <p className="text-xs text-amber-300/90">{currentWallet.name} · {t('wallet.wrongWallet')}</p>
          <button
            type="button"
            onClick={onConnect}
            disabled={connecting}
            className={clsx(
              sidebarBtnClass,
              'bg-[#38BDF8] text-white hover:bg-[#38BDF8]/90 disabled:opacity-50',
            )}
          >
            {connectLabel}
          </button>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-end gap-1">
        <Text variant="caption/medium" color="issue-dark">
          {currentWallet.name} · {t('wallet.wrongWallet')}
        </Text>
        <Button
          variant="primary"
          size="sm"
          onClick={onConnect}
          disabled={connecting}
          className="bg-[#2394c4] hover:bg-[#38BDF8] text-white rounded-lg px-2 py-2"
        >
          {connectLabel}
        </Button>
      </div>
    );
  }

  if (variant === 'sidebar') {
    return (
      <button
        type="button"
        onClick={onConnect}
        disabled={connecting}
        className={clsx(
          sidebarBtnClass,
          'bg-[#38BDF8] text-white hover:bg-[#38BDF8]/90 disabled:opacity-50',
        )}
      >
        {connectLabel}
      </button>
    );
  }

  return (
    <Button
      variant="primary"
      size="sm"
      onClick={onConnect}
      disabled={connecting}
      className="bg-[#2394c4] hover:bg-[#38BDF8] text-white rounded-lg px-2 py-2"
    >
      {connectLabel}
    </Button>
  );
}
